import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { Media, Reaction } from '../../api/types'
import { ChevronLeftIcon, ChevronRightIcon, ReplyIcon, SichernIcon, XIcon } from '../../components/Icons'
import { sichereFoto } from '../../lib/sichern'
import { Reactions } from './Reactions'

/**
 * Die Fotos eines Beitrags.
 *
 * Ein einzelnes Bild steht für sich und behält sein Seitenverhältnis -- ein
 * Hochformat vom Telefon in ein Quadrat zu schneiden nimmt gerade das weg,
 * worum es ging. Ab zwei Bildern wird daraus ein Raster aus Quadraten, denn
 * nebeneinander gestellte Bilder unterschiedlicher Form ergeben eine unruhige
 * Treppe.
 *
 * Ab dem fünften Bild zeigt die letzte Kachel, wie viele noch folgen. Die
 * Großansicht hat sie ohnehin alle.
 */
export function Gallery({
  media,
  reactions,
  canReact,
  onReact,
  onReply,
}: {
  media: Media[]
  reactions: Reaction[]
  canReact: boolean
  onReact: (emoji: string, mediaId: number) => void
  /**
   * Auf genau dieses Foto antworten.
   *
   * Der Knopf sitzt nur in der Großansicht und nicht auf der Kachel: die
   * öffnet die Großansicht, und ein zweites Ziel auf 56 Pixeln ist Gefummel.
   * Erst groß sieht man ohnehin, welches Bild man meint.
   */
  onReply?: (mediaId: number) => void
}) {
  const [open, setOpen] = useState<number | null>(null)

  if (media.length === 0) return null

  const fuer = (id: number) => reactions.filter((reaction) => reaction.media_id === id)

  const shown = media.length > 4 ? media.slice(0, 4) : media
  const hidden = media.length - shown.length

  return (
    <>
      <div
        className={media.length === 1 ? 'gallery gallery--single' : 'gallery'}
        style={media.length > 1 ? { gridTemplateColumns: `repeat(${media.length === 3 ? 3 : 2}, 1fr)` } : undefined}
      >
        {shown.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className="gallery__tile"
            onClick={() => setOpen(index)}
            aria-label={`Foto ${index + 1} von ${media.length} vergrößern`}
          >
            <img
              /* **Die Kachel nimmt das Vorschaubild** (#57). Sie ist rund 120
                 Punkte breit und lud bis hierher das volle Foto -- bei zwanzig
                 Fotos zwanzig Megabyte, und genau die wirft iOS als Erstes aus
                 dem Zwischenspeicher. Fehlt es (alles von vor #57), steht das
                 Original darin wie bisher. */
              src={item.thumb_url ?? item.url}
              alt=""
              loading="lazy"
              // Die Maße stehen in der Antwort. Der Platz ist damit reserviert,
              // bevor das Bild da ist, und der Verlauf springt beim Laden nicht.
              width={item.width}
              height={item.height}
            />
            {hidden > 0 && index === shown.length - 1 && (
              <span className="gallery__more">+{hidden}</span>
            )}

            {/* Was an diesem Foto hängt, steht auf dem Foto -- sonst wüsste
                bei vier Bildern niemand, welches gemeint ist. */}
            {fuer(item.id).length > 0 && (
              <span className="gallery__reactions">
                {fuer(item.id).map((reaction) => (
                  <span key={reaction.emoji}>
                    {reaction.emoji}
                    {reaction.count > 1 && reaction.count}
                  </span>
                ))}
              </span>
            )}
          </button>
        ))}
      </div>

      {open !== null && (
        <Lightbox
          media={media}
          start={open}
          reactionsFor={fuer}
          canReact={canReact}
          onReact={onReact}
          onReply={onReply}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  )
}

/**
 * Großansicht.
 *
 * Schließt mit Escape, Klick auf den Hintergrund oder Wischen. Geblättert wird
 * mit den Pfeiltasten -- am Telefon mit dem Daumen.
 *
 * **Sie wird an den Seitenkörper gehängt, nicht dorthin, wo sie steht.** Auf
 * der Kartenseite lag sie sonst unter der Karte (Leaflet baut seine Ebenen bis
 * Rang 1000) und im Formular wurde sie vom scrollenden Blatt abgeschnitten --
 * am iPad und am iPhone genau so gemeldet. Ein Bild in Großansicht gehört
 * keinem Kasten, es gehört dem Bildschirm.
 */
function Lightbox({
  media,
  start,
  reactionsFor,
  canReact,
  onReact,
  onReply,
  onClose,
}: {
  media: Media[]
  start: number
  reactionsFor: (mediaId: number) => Reaction[]
  canReact: boolean
  onReact: (emoji: string, mediaId: number) => void
  onReply?: (mediaId: number) => void
  onClose: () => void
}) {
  const [index, setIndex] = useState(start)
  /**
   * Wo der Finger aufsetzte — als Zettel, nicht als Zustand.
   *
   * **Hier stand `useState`, und das ist die falsche Schublade.** Der Wert
   * wird nirgends gezeichnet; gebraucht wird er nur im nächsten Ereignis. Als
   * Zustand hängt er daran, dass zwischen Aufsetzen und Loslassen wirklich
   * neu gezeichnet wurde — bei einem Finger stimmt das, bei einem schnellen
   * Wisch ist es Glück. `HighlightBlatt` macht es mit `griff` seit jeher so,
   * und genau dieser Unterschied fiel beim Nachmessen auf: dessen Blättern
   * löste aus, das der Großansicht nicht.
   */
  const griff = useRef<number | null>(null)

  /**
   * Was beim Sichern herausgekommen ist — als Satz, nicht als `title`.
   *
   * **Keine stille Absage:** wer den Knopf antippt und nichts sieht, hält die
   * Anwendung für kaputt. Auf dem Telefon spricht meist das Teilen-Blatt für
   * sich; dort bleibt es still. Gesagt wird, was der Mensch sonst nicht sieht:
   * dass es lief, dass es geladen wurde, oder dass es schiefging.
   */
  const [meldung, setMeldung] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  // Ein neues Bild, eine neue Lage -- „Heruntergeladen." unter dem nächsten
  // Foto wäre eine Auskunft über das vorige.
  useEffect(() => setMeldung(null), [index])

  const back = () => setIndex((i) => (i - 1 + media.length) % media.length)
  const forward = () => setIndex((i) => (i + 1) % media.length)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft') back()
      if (event.key === 'ArrowRight') forward()
    }
    window.addEventListener('keydown', onKey)

    // Der Verlauf darunter soll nicht mitscrollen, während oben ein Bild steht.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.length])

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Foto in Großansicht"
      onClick={onClose}
      /*
       * **Die Geste endet hier.**
       *
       * Gemeldet aus dem Betrieb: „wenn aus der Highlight-Detailkarte die
       * Fotos geöffnet werden, wird beim Wischen zum nächsten Highlight
       * gewechselt und nicht zum nächsten Foto. Da wird wohl an das falsche
       * Fenster weitergegeben." Genau so war es.
       *
       * **Und der Grund ist eine Eigenheit von `createPortal`:** im DOM hängt
       * dieses Blatt an `document.body`, aber Reacts Ereignisse steigen
       * weiterhin den **React-Baum** hinauf — also dorthin, wo `<Gallery>` im
       * Quelltext steht. Das ist in `HighlightBlatt` mitten im Blatt, und
       * dessen `onTouchEnd` blättert bei einem waagerechten Wisch zum
       * nächsten Punkt. Ein Wisch, zwei Ebenen.
       *
       * **Deshalb steht die Sperre hier und nicht dort.** Die Großansicht
       * liegt obenauf; ihre Gesten sind ihre. So gilt es für jede Ebene
       * darunter, auch für die nächste, die jemand baut — eine Vorsichtsregel
       * in `HighlightBlatt` hätte nur diesen einen Fall gedeckt.
       *
       * **Die Tastatur bleibt davon unberührt** und braucht ihre eigene
       * Sperre (`HighlightBlatt`, `document.querySelector('.lightbox')`): dort
       * hängen beide Zuhörer unmittelbar an `window`, da steigt nichts auf,
       * was man anhalten könnte.
       */
      onTouchStart={(event) => {
        event.stopPropagation()
        griff.current = event.touches[0]?.clientX ?? null
      }}
      onTouchEnd={(event) => {
        event.stopPropagation()
        const start = griff.current
        griff.current = null
        if (start === null) return
        const delta = (event.changedTouches[0]?.clientX ?? start) - start
        // Unter 50 Pixeln war es ein Tippen und kein Wischen.
        if (delta > 50) back()
        else if (delta < -50) forward()
      }}
    >
      {/* **Eine eigene Zeile und kein Schweben.** Über einem hohen Foto —
          einem Bildschirmfoto etwa — lag der Knopf vorher mitten im Bild:
          weiß auf hell und damit weder zu sehen noch zu treffen. */}
      <div className="lightbox__kopf">
        <button type="button" className="lightbox__close" onClick={onClose} aria-label="Schließen">
          <XIcon size={22} />
        </button>
      </div>

      {media.length > 1 && (
        <>
          <button
            type="button"
            className="lightbox__step lightbox__step--back"
            onClick={(event) => {
              event.stopPropagation()
              back()
            }}
            aria-label="Vorheriges Foto"
          >
            <ChevronLeftIcon size={26} />
          </button>
          <button
            type="button"
            className="lightbox__step lightbox__step--forward"
            onClick={(event) => {
              event.stopPropagation()
              forward()
            }}
            aria-label="Nächstes Foto"
          >
            <ChevronRightIcon size={26} />
          </button>
        </>
      )}

      {/* **Der Kasten dazwischen ist nicht schmückend.** Ein Bild als
          unmittelbares Kind der Rasterzelle rechnet seinen Deckel
          (`max-height: 100%`) gegen die Zelle -- und WebKit tut das nicht.
          Über einem Bildschirmfoto lag es deshalb über Kreuz und Leiste. Der
          Kasten hat eine gewöhnliche Höhe und schneidet notfalls ab. */}
      <div className="lightbox__flaeche">
        <img
          className="lightbox__image"
          src={media[index]?.url}
          alt=""
          onClick={(event) => event.stopPropagation()}
        />
      </div>

      <div className="lightbox__bar" onClick={(event) => event.stopPropagation()}>
        {media[index] && (canReact || reactionsFor(media[index].id).length > 0) && (
          <Reactions
            compact
            reactions={reactionsFor(media[index].id)}
            onPick={(emoji) => canReact && onReact(emoji, media[index]!.id)}
          />
        )}

        <div className="lightbox__aktionen">
          {/*
            **Sichern gehört der Anwendung und nicht der Geste** (#58). Auf iOS
            legt ein langes Drücken das Bild in „Fotos", auf Android passiert
            dabei nichts — und wo Chrome dort doch ein Menü zeigt, landet die
            Datei in *Downloads* und nicht in der Galerie. Der Knopf tut auf
            beiden Geräten dasselbe: er reicht das Bild ans System weiter.
          */}
          {media[index] && (
            <button
              type="button"
              className="lightbox__antwort"
              disabled={laeuft}
              onClick={async () => {
                const bild = media[index]
                if (bild === undefined) return
                setLaeuft(true)
                setMeldung(null)
                try {
                  const weg = await sichereFoto(bild)
                  /*
                   * **Nach dem Teilen-Blatt bleibt es still** — das Gerät hat
                   * schon geantwortet, ein zweiter Satz wäre Geplapper.
                   *
                   * **Nach einem Download steht dagegen die eigentliche Frage
                   * im Raum: wo ist es hin?** Auf Android landet es im Ordner
                   * *Downloads*, und den zeigt jede Galerie-App als Album —
                   * das weiß nur, wer es gesagt bekommt. „Heruntergeladen."
                   * stand hier vorher und beantwortete genau das nicht.
                   */
                  if (weg === 'geladen') {
                    setMeldung('Im Ordner „Downloads" gespeichert — dort findet ihn auch die Galerie.')
                  }
                } catch {
                  setMeldung('Das Foto ließ sich nicht holen. Ohne Verbindung geht es nicht.')
                } finally {
                  setLaeuft(false)
                }
              }}
            >
              <SichernIcon size={16} />
              {laeuft ? 'Einen Moment …' : 'Foto sichern'}
            </button>
          )}

          {onReply && media[index] && (
            <button
              type="button"
              className="lightbox__antwort"
              onClick={() => {
                onReply(media[index]!.id)
                // Zumachen gehört dazu: geantwortet wird unten im Schreibfeld,
                // und das liegt hinter diesem Bild.
                onClose()
              }}
            >
              <ReplyIcon size={16} />
              Auf dieses Foto antworten
            </button>
          )}
        </div>

        {meldung !== null && (
          <p className="lightbox__meldung" role="alert">
            {meldung}
          </p>
        )}
        {media.length > 1 && (
          <span className="lightbox__count">
            {index + 1} von {media.length}
          </span>
        )}
      </div>
    </div>,
    document.body,
  )
}
